import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../services/api';
import usePermissions from '../hooks/usePermissions';

const SETTINGS_DOMAIN_PERMISSIONS = {
  pricing: ['manage_settings', 'manage_settings_pricing'],
  fleet: ['manage_settings', 'manage_settings_fleet'],
  safety: ['manage_settings', 'manage_settings_safety'],
  support: ['manage_settings', 'manage_settings_support'],
};

const Settings = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const { section: routeSection } = useParams();

  const [rideTypes, setRideTypes] = useState([]);
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [driverCommissionPercent, setDriverCommissionPercent] = useState('80');
  const [cancellationPolicy, setCancellationPolicy] = useState({
    riderCancellationFee: '5.00',
    feeStages: ['arriving', 'arrived'],
    driverPenaltyEnabled: false,
    driverPenaltyType: 'fixed',
    driverPenaltyAmount: '0',
  });
  const [fareOveragePolicy, setFareOveragePolicy] = useState({
    capPercent: '20',
    capFixed: '5',
    arrearsBlockThreshold: '30',
  });
  const [loadingRideTypes, setLoadingRideTypes] = useState(false);
  const [savingRideTypes, setSavingRideTypes] = useState(false);
  const [carCategoryRules, setCarCategoryRules] = useState([]);
  const [carRuleSearch, setCarRuleSearch] = useState('');
  const [carRulePage, setCarRulePage] = useState(1);
  const [editingCarRuleIndex, setEditingCarRuleIndex] = useState(null);
  const [newCarRule, setNewCarRule] = useState({
    make: '',
    model: '',
    allowedCategories: [],
    excludedCategories: [],
  });
  const [loadingCarRules, setLoadingCarRules] = useState(false);
  const [savingCarRules, setSavingCarRules] = useState(false);
  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptionsByMake, setModelOptionsByMake] = useState({});
  const [carCatalogSyncStatus, setCarCatalogSyncStatus] = useState(null);
  const [startingCatalogSync, setStartingCatalogSync] = useState(false);
  const [refreshingMakes, setRefreshingMakes] = useState(false);
  const makeSearchTimerRef = useRef(null);
  const [safetySettings, setSafetySettings] = useState({
    safetyCenterEnabled: true,
    sosEnabled: true,
    rideSharingEnabled: true,
    trustedContactsEnabled: true,
    trustedContactsMax: '5',
    safetyTipsEnabled: true,
    emergencyNumber: '999',
    incidentReportEnabled: true,
    liveTrackingEnabled: true,
    locationShareIntervalSec: '10',
  });
  const [loadingSafetySettings, setLoadingSafetySettings] = useState(false);
  const [savingSafetySettings, setSavingSafetySettings] = useState(false);
  const [registrationSettings, setRegistrationSettings] = useState({
    riderGenderSelectionEnabled: false,
    driverGenderSelectionEnabled: false,
  });
  const [loadingRegistrationSettings, setLoadingRegistrationSettings] = useState(false);
  const [savingRegistrationSettings, setSavingRegistrationSettings] = useState(false);
  const [supportSettings, setSupportSettings] = useState({
    maxAttachmentsPerMessage: '3',
    driverIncidentTypes: [],
  });
  const [loadingSupportSettings, setLoadingSupportSettings] = useState(false);
  const [savingSupportSettings, setSavingSupportSettings] = useState(false);
  const { can } = usePermissions();

  const hasAdminPermission = can;

  const canManagePricingSettings = useMemo(
    () => SETTINGS_DOMAIN_PERMISSIONS.pricing.some((permission) => hasAdminPermission(permission)),
    [hasAdminPermission]
  );

  const canManageFleetSettings = useMemo(
    () => SETTINGS_DOMAIN_PERMISSIONS.fleet.some((permission) => hasAdminPermission(permission)),
    [hasAdminPermission]
  );

  const canManageSafetySettings = useMemo(
    () => SETTINGS_DOMAIN_PERMISSIONS.safety.some((permission) => hasAdminPermission(permission)),
    [hasAdminPermission]
  );

  const canManageRegistrationSettings = useMemo(
    () => hasAdminPermission('manage_settings'),
    [hasAdminPermission]
  );

  const canManageSupportPolicySettings = useMemo(
    () => SETTINGS_DOMAIN_PERMISSIONS.support.some((permission) => hasAdminPermission(permission)),
    [hasAdminPermission]
  );

  const availableSettingsSections = useMemo(() => {
    const sections = [{ key: 'general', label: 'General' }];
    if (canManagePricingSettings) sections.push({ key: 'pricing', label: 'Pricing' });
    if (canManageFleetSettings) sections.push({ key: 'fleet', label: 'Fleet' });
    if (canManageSafetySettings) sections.push({ key: 'safety', label: 'Safety' });
    if (canManageSupportPolicySettings) sections.push({ key: 'support', label: 'Support Policy' });
    return sections;
  }, [canManagePricingSettings, canManageFleetSettings, canManageSafetySettings, canManageSupportPolicySettings]);

  const activeSection = useMemo(() => {
    const requested = String(routeSection || '').trim().toLowerCase();
    if (availableSettingsSections.some((item) => item.key === requested)) {
      return requested;
    }
    return availableSettingsSections[0]?.key || 'general';
  }, [routeSection, availableSettingsSections]);

  const CAR_RULES_PAGE_SIZE = 10;

  const filteredCarRuleIndices = useMemo(() => {
    const query = String(carRuleSearch || '').trim().toLowerCase();
    return carCategoryRules
      .map((rule, index) => ({
        index,
        haystack: `${String(rule.make || '')} ${String(rule.model || '')}`.toLowerCase(),
      }))
      .filter((item) => !query || item.haystack.includes(query))
      .map((item) => item.index);
  }, [carCategoryRules, carRuleSearch]);

  const totalCarRulePages = Math.max(1, Math.ceil(filteredCarRuleIndices.length / CAR_RULES_PAGE_SIZE));

  const pagedCarRuleIndices = useMemo(() => {
    const start = (carRulePage - 1) * CAR_RULES_PAGE_SIZE;
    return filteredCarRuleIndices.slice(start, start + CAR_RULES_PAGE_SIZE);
  }, [filteredCarRuleIndices, carRulePage]);

  const loadRideTypes = async () => {
    setLoadingRideTypes(true);
    try {
      const response = await api.get('/admin/ride-types');
      if (response.data?.success) {
        const items = response.data?.data?.rideTypes || [];
        setCurrencyCode(String(response.data?.data?.currencyCode || 'QAR').toUpperCase());
        setDriverCommissionPercent(String(response.data?.data?.driverCommissionPercent ?? 80));
        const cp = response.data?.data?.cancellationPolicy || {};
        setCancellationPolicy({
          riderCancellationFee: String(cp.riderCancellationFee ?? '5.00'),
          feeStages: Array.isArray(cp.feeStages) ? cp.feeStages : ['arriving', 'arrived'],
          driverPenaltyEnabled: cp.driverPenaltyEnabled === true,
          driverPenaltyType: cp.driverPenaltyType || 'fixed',
          driverPenaltyAmount: String(cp.driverPenaltyAmount ?? '0'),
        });
        const fop = response.data?.data?.fareOveragePolicy || {};
        setFareOveragePolicy({
          capPercent: String(fop.capPercent ?? 20),
          capFixed: String(fop.capFixed ?? 5),
          arrearsBlockThreshold: String(fop.arrearsBlockThreshold ?? 30),
        });
        setRideTypes(
          items.map((t, idx) => ({
            key: t.key ?? '',
            label: t.label ?? '',
            baseFare: String(t.baseFare ?? ''),
            perKm: String(t.perKm ?? ''),
            perMin: String(t.perMin ?? ''),
            isActive: t.isActive !== false,
            sortOrder: String(t.sortOrder ?? idx + 1),
          }))
        );
      } else {
        toast.error(response.data?.message || 'Failed to load ride types');
      }
    } catch (e) {
      toast.error('Failed to load ride types');
    } finally {
      setLoadingRideTypes(false);
    }
  };

  const categoryOptions = useMemo(
    () =>
      rideTypes
        .filter((item) => String(item.key || '').trim())
        .map((item) => ({ value: String(item.key).trim(), label: item.label || item.key })),
    [rideTypes]
  );

  const getModelOptions = (make) => {
    const normalizedMake = String(make || '').trim();
    if (!normalizedMake) return [];
    return Array.isArray(modelOptionsByMake[normalizedMake]) ? modelOptionsByMake[normalizedMake] : [];
  };

  const loadCarCategoryRules = async () => {
    setLoadingCarRules(true);
    try {
      const response = await api.get('/admin/car-category-rules');
      if (response.data?.success) {
        const rules = Array.isArray(response.data?.data?.rules) ? response.data.data.rules : [];
        const normalizedRules = rules.map((rule) => ({
          make: String(rule.make || ''),
          model: String(rule.model || ''),
          allowedCategories: Array.isArray(rule.allowedCategories) ? rule.allowedCategories : [],
          excludedCategories: Array.isArray(rule.excludedCategories) ? rule.excludedCategories : [],
        }));

        setCarCategoryRules(normalizedRules);

        const uniqueMakes = [...new Set(normalizedRules.map((rule) => String(rule.make || '').trim()).filter(Boolean))];
        if (uniqueMakes.length > 0) {
          await Promise.all(uniqueMakes.map((make) => ensureModelOptions(make)));
        }
      } else {
        toast.error(response.data?.message || 'Failed to load car category rules');
      }
    } catch (_e) {
      toast.error('Failed to load car category rules');
    } finally {
      setLoadingCarRules(false);
    }
  };

  const loadMakeOptions = async ({ query = '', limit = 100 } = {}) => {
    setRefreshingMakes(true);
    try {
      const response = await api.get('/admin/car-catalog/makes', {
        params: {
          scope: 'all',
          q: String(query || '').trim(),
          limit,
        },
      });
      const makes = Array.isArray(response?.data?.data?.makes) ? response.data.data.makes : [];
      const options = makes
        .map((item) => String(item || '').trim())
        .filter(Boolean)
        .map((name) => ({ value: name, label: name }));
      setMakeOptions(options);
    } catch (_e) {
      toast.error('Failed to load car makes list');
    } finally {
      setRefreshingMakes(false);
    }
  };

  const searchMakeOptions = (text) => {
    if (makeSearchTimerRef.current) {
      clearTimeout(makeSearchTimerRef.current);
    }

    makeSearchTimerRef.current = setTimeout(() => {
      loadMakeOptions({ query: text, limit: 100 });
    }, 250);
  };

  const ensureModelOptions = async (make) => {
    const normalizedMake = String(make || '').trim();
    if (!normalizedMake || modelOptionsByMake[normalizedMake]) return;

    try {
      const response = await api.get('/admin/car-catalog/models', {
        params: {
          make: normalizedMake,
        },
      });
      const models = Array.isArray(response?.data?.data?.models) ? response.data.data.models : [];
      const options = models
        .map((item) => String(item || '').trim())
        .filter(Boolean)
        .map((name) => ({ value: name, label: name }));

      setModelOptionsByMake((prev) => ({
        ...prev,
        [normalizedMake]: options,
      }));
    } catch (_e) {
      setModelOptionsByMake((prev) => ({
        ...prev,
        [normalizedMake]: [],
      }));
    }
  };

  const loadCarCatalogSyncStatus = async ({ silent = false } = {}) => {
    try {
      const response = await api.get('/admin/car-catalog/sync-status');
      if (response.data?.success) {
        setCarCatalogSyncStatus(response.data?.data || null);
      }
    } catch (_e) {
      if (!silent) {
        toast.error('Failed to load catalog sync status');
      }
    }
  };

  const startCarCatalogSync = async () => {
    if (!canManageFleetSettings || startingCatalogSync) return;
    setStartingCatalogSync(true);
    try {
      const response = await api.post('/admin/car-catalog/sync-all');
      if (response.data?.success) {
        const started = !!response.data?.data?.started;
        setCarCatalogSyncStatus(response.data?.data?.status || null);
        toast.success(started ? 'Catalog sync started in background' : 'Catalog sync already running');
      } else {
        toast.error(response.data?.message || 'Failed to start catalog sync');
      }
    } catch (_e) {
      toast.error('Failed to start catalog sync');
    } finally {
      setStartingCatalogSync(false);
    }
  };

  useEffect(() => {
    if (canManagePricingSettings) {
      loadRideTypes();
    }
    if (canManageSafetySettings) {
      loadSafetySettings();
    }
    if (canManageSupportPolicySettings) {
      loadSupportSettings();
    }
    if (canManageFleetSettings) {
      loadCarCategoryRules();
      loadMakeOptions({ limit: 100 });
      loadCarCatalogSyncStatus({ silent: true });
    }
    if (canManageRegistrationSettings) {
      loadRegistrationSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManagePricingSettings, canManageSafetySettings, canManageSupportPolicySettings, canManageFleetSettings, canManageRegistrationSettings]);

  useEffect(() => {
    const requested = String(routeSection || '').trim().toLowerCase();
    if (!requested || requested !== activeSection) {
      navigate(`/settings/${activeSection}`, { replace: true });
    }
  }, [routeSection, activeSection, navigate]);

  useEffect(() => {
    setCarRulePage(1);
  }, [carRuleSearch]);

  useEffect(() => {
    if (carRulePage > totalCarRulePages) {
      setCarRulePage(totalCarRulePages);
    }
  }, [carRulePage, totalCarRulePages]);

  useEffect(() => {
    if (editingCarRuleIndex == null) return;
    if (!carCategoryRules[editingCarRuleIndex]) {
      setEditingCarRuleIndex(null);
    }
  }, [editingCarRuleIndex, carCategoryRules]);

  useEffect(() => {
    return () => {
      if (makeSearchTimerRef.current) {
        clearTimeout(makeSearchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!carCatalogSyncStatus?.isRunning) return undefined;

    const timer = setInterval(() => {
      loadCarCatalogSyncStatus({ silent: true });
    }, 5000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carCatalogSyncStatus?.isRunning]);

  const loadSafetySettings = async () => {
    setLoadingSafetySettings(true);
    try {
      const response = await api.get('/admin/safety-settings');
      if (response.data?.success) {
        const s = response.data?.data?.settings || {};
        setSafetySettings({
          safetyCenterEnabled: s.safetyCenterEnabled !== false,
          sosEnabled: s.sosEnabled !== false,
          rideSharingEnabled: s.rideSharingEnabled !== false,
          trustedContactsEnabled: s.trustedContactsEnabled !== false,
          trustedContactsMax: String(s.trustedContactsMax ?? 5),
          safetyTipsEnabled: s.safetyTipsEnabled !== false,
          emergencyNumber: String(s.emergencyNumber ?? '999'),
          incidentReportEnabled: s.incidentReportEnabled !== false,
          liveTrackingEnabled: s.liveTrackingEnabled !== false,
          locationShareIntervalSec: String(s.locationShareIntervalSec ?? 10),
        });
      } else {
        toast.error(response.data?.message || 'Failed to load safety settings');
      }
    } catch (e) {
      toast.error('Failed to load safety settings');
    } finally {
      setLoadingSafetySettings(false);
    }
  };

  const updateSafetySetting = (patch) => {
    setSafetySettings((prev) => ({ ...prev, ...patch }));
  };

  const saveSafetySettings = async () => {
    if (!canManageSafetySettings) {
      toast.error('You do not have permission to update settings');
      return;
    }

    setSavingSafetySettings(true);
    try {
      const payload = {
        settings: {
          safetyCenterEnabled: safetySettings.safetyCenterEnabled !== false,
          sosEnabled: safetySettings.sosEnabled !== false,
          rideSharingEnabled: safetySettings.rideSharingEnabled !== false,
          trustedContactsEnabled: safetySettings.trustedContactsEnabled !== false,
          trustedContactsMax: Number(safetySettings.trustedContactsMax),
          safetyTipsEnabled: safetySettings.safetyTipsEnabled !== false,
          emergencyNumber: String(safetySettings.emergencyNumber || '').trim(),
          incidentReportEnabled: safetySettings.incidentReportEnabled !== false,
          liveTrackingEnabled: safetySettings.liveTrackingEnabled !== false,
          locationShareIntervalSec: Number(safetySettings.locationShareIntervalSec),
        },
      };

      const response = await api.put('/admin/safety-settings', payload);
      if (response.data?.success) {
        toast.success('Safety settings saved');
        await loadSafetySettings();
      } else {
        toast.error(response.data?.message || 'Failed to save safety settings');
      }
    } catch (e) {
      const apiMessage = e?.response?.data?.message;
      toast.error(apiMessage || 'Failed to save safety settings');
    } finally {
      setSavingSafetySettings(false);
    }
  };

  const loadRegistrationSettings = async () => {
    setLoadingRegistrationSettings(true);
    try {
      const response = await api.get('/admin/registration-settings');
      if (response.data?.success) {
        const s = response.data?.data?.settings || {};
        setRegistrationSettings({
          riderGenderSelectionEnabled: s.riderGenderSelectionEnabled === true,
          driverGenderSelectionEnabled: s.driverGenderSelectionEnabled === true,
        });
      } else {
        toast.error(response.data?.message || 'Failed to load registration settings');
      }
    } catch (e) {
      toast.error('Failed to load registration settings');
    } finally {
      setLoadingRegistrationSettings(false);
    }
  };

  const updateRegistrationSetting = (patch) => {
    setRegistrationSettings((prev) => ({ ...prev, ...patch }));
  };

  const saveRegistrationSettings = async () => {
    if (!canManageRegistrationSettings) {
      toast.error('You do not have permission to update settings');
      return;
    }

    setSavingRegistrationSettings(true);
    try {
      const payload = {
        settings: {
          riderGenderSelectionEnabled: registrationSettings.riderGenderSelectionEnabled === true,
          driverGenderSelectionEnabled: registrationSettings.driverGenderSelectionEnabled === true,
        },
      };

      const response = await api.put('/admin/registration-settings', payload);
      if (response.data?.success) {
        toast.success('Registration settings saved');
        await loadRegistrationSettings();
      } else {
        toast.error(response.data?.message || 'Failed to save registration settings');
      }
    } catch (e) {
      const apiMessage = e?.response?.data?.message;
      toast.error(apiMessage || 'Failed to save registration settings');
    } finally {
      setSavingRegistrationSettings(false);
    }
  };

  const loadSupportSettings = async () => {
    setLoadingSupportSettings(true);
    try {
      const response = await api.get('/admin/support-settings');
      if (response.data?.success) {
        const s = response.data?.data?.settings || {};
        const incidentTypes = Array.isArray(s.driverIncidentTypes) ? s.driverIncidentTypes : [];
        setSupportSettings({
          maxAttachmentsPerMessage: String(s.maxAttachmentsPerMessage ?? 3),
          driverIncidentTypes: incidentTypes.map((item, idx) => ({
            key: item.key ?? '',
            label: item.label ?? '',
            isActive: item.isActive !== false,
            sortOrder: String(item.sortOrder ?? idx + 1),
          })),
        });
      } else {
        toast.error(response.data?.message || 'Failed to load support settings');
      }
    } catch (e) {
      toast.error('Failed to load support settings');
    } finally {
      setLoadingSupportSettings(false);
    }
  };

  const updateIncidentType = (index, patch) => {
    setSupportSettings((prev) => ({
      ...prev,
      driverIncidentTypes: prev.driverIncidentTypes.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const addIncidentType = () => {
    setSupportSettings((prev) => ({
      ...prev,
      driverIncidentTypes: [
        ...prev.driverIncidentTypes,
        {
          key: '',
          label: '',
          isActive: true,
          sortOrder: String(prev.driverIncidentTypes.length + 1),
        },
      ],
    }));
  };

  const removeIncidentType = (index) => {
    setSupportSettings((prev) => ({
      ...prev,
      driverIncidentTypes: prev.driverIncidentTypes.filter((_, i) => i !== index),
    }));
  };

  const saveSupportSettings = async () => {
    if (!canManageSupportPolicySettings) {
      toast.error('You do not have permission to update settings');
      return;
    }

    setSavingSupportSettings(true);
    try {
      const payload = {
        settings: {
          maxAttachmentsPerMessage: Number(supportSettings.maxAttachmentsPerMessage),
          driverIncidentTypes: supportSettings.driverIncidentTypes.map((item, idx) => ({
            key: String(item.key || '').trim().toLowerCase(),
            label: String(item.label || '').trim(),
            isActive: item.isActive !== false,
            sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : idx + 1,
          })),
        },
      };

      const response = await api.put('/admin/support-settings', payload);
      if (response.data?.success) {
        toast.success('Support settings saved');
        await loadSupportSettings();
      } else {
        toast.error(response.data?.message || 'Failed to save support settings');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save support settings');
    } finally {
      setSavingSupportSettings(false);
    }
  };

  const updateRideType = (index, patch) => {
    setRideTypes((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const addRideType = () => {
    setRideTypes((prev) => [
      ...prev,
      {
        key: '',
        label: '',
        baseFare: '0',
        perKm: '0',
        perMin: '0',
        isActive: true,
        sortOrder: String(prev.length + 1),
      },
    ]);
  };

  const removeRideType = (index) => {
    setRideTypes((prev) => prev.filter((_, i) => i !== index));
  };

  const saveRideTypes = async () => {
    if (!canManagePricingSettings) {
      toast.error('You do not have permission to update settings');
      return;
    }

    setSavingRideTypes(true);
    try {
      const payload = {
        currencyCode: String(currencyCode || 'QAR').trim().toUpperCase(),
        driverCommissionPercent: Number(driverCommissionPercent),
        cancellationPolicy: {
          riderCancellationFee: Number(cancellationPolicy.riderCancellationFee),
          feeStages: cancellationPolicy.feeStages,
          driverPenaltyEnabled: cancellationPolicy.driverPenaltyEnabled,
          driverPenaltyType: cancellationPolicy.driverPenaltyType,
          driverPenaltyAmount: Number(cancellationPolicy.driverPenaltyAmount),
        },
        fareOveragePolicy: {
          capPercent: Number(fareOveragePolicy.capPercent),
          capFixed: Number(fareOveragePolicy.capFixed),
          arrearsBlockThreshold: Number(fareOveragePolicy.arrearsBlockThreshold),
        },
        rideTypes: rideTypes.map((t, idx) => ({
          key: String(t.key || '').trim(),
          label: String(t.label || '').trim(),
          baseFare: Number(t.baseFare),
          perKm: Number(t.perKm),
          perMin: Number(t.perMin),
          isActive: t.isActive !== false,
          sortOrder: Number.isFinite(Number(t.sortOrder)) ? Number(t.sortOrder) : idx + 1,
        })),
      };

      const response = await api.put('/admin/ride-types', payload);
      if (response.data?.success) {
        toast.success('Fare configuration saved');
        await loadRideTypes();
      } else {
        toast.error(response.data?.message || 'Failed to save configuration');
      }
    } catch (e) {
      const apiMessage = e?.response?.data?.message;
      toast.error(apiMessage || 'Failed to save configuration');
    } finally {
      setSavingRideTypes(false);
    }
  };

  const updateCarRule = (index, patch) => {
    setCarCategoryRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const applyCategoryToggle = (rule, listName, categoryValue, checked) => {
    const nextRule = {
      ...rule,
      allowedCategories: Array.isArray(rule.allowedCategories) ? [...rule.allowedCategories] : [],
      excludedCategories: Array.isArray(rule.excludedCategories) ? [...rule.excludedCategories] : [],
    };

    const targetList = listName === 'allowed' ? nextRule.allowedCategories : nextRule.excludedCategories;
    const otherList = listName === 'allowed' ? nextRule.excludedCategories : nextRule.allowedCategories;

    const targetSet = new Set(targetList);
    if (checked) {
      targetSet.add(categoryValue);
      const otherSet = new Set(otherList.filter((item) => item !== categoryValue));
      if (listName === 'allowed') {
        nextRule.excludedCategories = Array.from(otherSet);
      } else {
        nextRule.allowedCategories = Array.from(otherSet);
      }
    } else {
      targetSet.delete(categoryValue);
    }

    if (listName === 'allowed') {
      nextRule.allowedCategories = Array.from(targetSet);
    } else {
      nextRule.excludedCategories = Array.from(targetSet);
    }

    return nextRule;
  };

  const toggleCarRuleCategory = (index, listName, categoryValue, checked) => {
    setCarCategoryRules((prev) =>
      prev.map((rule, i) => {
        if (i !== index) return rule;
        return applyCategoryToggle(rule, listName, categoryValue, checked);
      })
    );
  };

  const toggleNewCarRuleCategory = (listName, categoryValue, checked) => {
    setNewCarRule((prev) => applyCategoryToggle(prev, listName, categoryValue, checked));
  };

  const addCarRule = () => {
    const make = String(newCarRule.make || '').trim();
    if (!make) {
      toast.error('Please select car make before adding rule');
      return;
    }

    setCarCategoryRules((prev) => {
      const next = [
        ...prev,
        {
          make,
          model: String(newCarRule.model || '').trim(),
          allowedCategories: Array.isArray(newCarRule.allowedCategories) ? newCarRule.allowedCategories : [],
          excludedCategories: Array.isArray(newCarRule.excludedCategories) ? newCarRule.excludedCategories : [],
        },
      ];
      setEditingCarRuleIndex(next.length - 1);
      return next;
    });

    setNewCarRule({
      make: '',
      model: '',
      allowedCategories: [],
      excludedCategories: [],
    });
  };

  const removeCarRule = (index) => {
    setCarCategoryRules((prev) => prev.filter((_, i) => i !== index));
    setEditingCarRuleIndex((prev) => {
      if (prev == null) return prev;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const saveCarCategoryRules = async () => {
    if (!canManageFleetSettings) {
      toast.error('You do not have permission to update settings');
      return;
    }

    setSavingCarRules(true);
    try {
      const payload = {
        rules: carCategoryRules.map((rule) => ({
          make: String(rule.make || '').trim(),
          model: String(rule.model || '').trim(),
          allowedCategories: Array.isArray(rule.allowedCategories) ? rule.allowedCategories : [],
          excludedCategories: Array.isArray(rule.excludedCategories) ? rule.excludedCategories : [],
        })),
      };

      const response = await api.put('/admin/car-category-rules', payload);
      if (response.data?.success) {
        toast.success('Car category rules saved');
        await loadCarCategoryRules();
      } else {
        toast.error(response.data?.message || 'Failed to save car category rules');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save car category rules');
    } finally {
      setSavingCarRules(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Settings</h1>

      <div className="card">
        <div className="flex flex-wrap gap-2">
          {availableSettingsSections.map((item) => (
            <button
              key={item.key}
              className={`px-3 py-2 rounded-md text-sm font-medium border ${
                activeSection === item.key
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => navigate(`/settings/${item.key}`)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Settings */}
      {activeSection === 'general' && (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Profile Settings</h3>
        <div className="max-w-md space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input type="text" defaultValue={admin?.firstName} className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input type="text" defaultValue={admin?.lastName} className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" defaultValue={admin?.email} className="input" disabled />
          </div>
          <button className="btn-primary">Update Profile</button>
        </div>
      </div>
      )}

      {/* Password Settings */}
      {activeSection === 'general' && (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Change Password</h3>
        <div className="max-w-md space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input type="password" className="input" placeholder="••••••••" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input type="password" className="input" placeholder="••••••••" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input type="password" className="input" placeholder="••••••••" />
          </div>
          <button className="btn-primary">Change Password</button>
        </div>
      </div>
      )}

      {/* App Settings */}
      {activeSection === 'general' && (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Application Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-gray-500">Receive email notifications for new ride requests</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-gray-500">Receive push notifications for important updates</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-gray-500">Use dark theme for the dashboard</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
        </div>
      </div>
      )}

      {/* Registration Settings — gender-selection toggles, off by default */}
      {activeSection === 'general' && canManageRegistrationSettings && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Registration Settings</h3>
            <button
              className="btn-primary"
              onClick={saveRegistrationSettings}
              disabled={!canManageRegistrationSettings || loadingRegistrationSettings || savingRegistrationSettings}
            >
              {savingRegistrationSettings ? 'Saving…' : 'Save Registration Settings'}
            </button>
          </div>

          {loadingRegistrationSettings ? (
            <div className="text-sm text-gray-500">Loading registration settings…</div>
          ) : (
            <div className="space-y-4">
              {[
                {
                  key: 'riderGenderSelectionEnabled',
                  label: 'Rider Gender Selection Enabled',
                  description: 'Show a gender picker (Female/Male) on rider and teen registration. Off by default — all riders register as Female.',
                },
                {
                  key: 'driverGenderSelectionEnabled',
                  label: 'Driver Gender Selection Enabled',
                  description: 'Show a gender picker (Female/Male) on driver registration. Off by default — all drivers register as Female.',
                },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3 border-b">
                  <div className="pr-4">
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!registrationSettings[item.key]}
                      onChange={(e) => updateRegistrationSetting({ [item.key]: e.target.checked })}
                      disabled={!canManageRegistrationSettings || savingRegistrationSettings}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fare Settings */}
      {activeSection === 'pricing' && canManagePricingSettings && (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Fare Configuration</h3>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={addRideType} disabled={!canManagePricingSettings || loadingRideTypes || savingRideTypes}>
              Add Ride Type
            </button>
            <button className="btn-primary" onClick={saveRideTypes} disabled={!canManagePricingSettings || loadingRideTypes || savingRideTypes}>
              {savingRideTypes ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
          <div className="flex items-center gap-3">
            <select
              value={currencyCode}
              onChange={(e) => setCurrencyCode(e.target.value)}
              className="input max-w-xs"
              disabled={!canManagePricingSettings || loadingRideTypes || savingRideTypes}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="CAD">CAD</option>
              <option value="AUD">AUD</option>
              <option value="SAR">SAR</option>
              <option value="AED">AED</option>
              <option value="KWD">KWD</option>
              <option value="QAR">QAR</option>
              <option value="BHD">BHD</option>
              <option value="JOD">JOD</option>
              <option value="EGP">EGP</option>
            </select>
            <p className="text-sm text-gray-500">Used across fare estimates, rides, and reports.</p>
          </div>
        </div>

        <div className="mb-4 max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">Driver Commission (%)</label>
          <input
            type="number"
            min="1"
            max="99"
            step="0.1"
            value={driverCommissionPercent}
            onChange={(e) => setDriverCommissionPercent(e.target.value)}
            className="input"
            disabled={!canManagePricingSettings || loadingRideTypes || savingRideTypes}
          />
          <p className="text-sm text-gray-500 mt-1">
            Driver share of each fare. Platform share is calculated automatically.
          </p>
        </div>

        {loadingRideTypes ? (
          <div className="text-sm text-gray-500">Loading fare configuration…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rideTypes.map((t, idx) => (
              <div key={`${t.key}-${idx}`} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Ride Type</div>
                  <button
                    className="btn-secondary"
                    onClick={() => removeRideType(idx)}
                    disabled={!canManagePricingSettings || savingRideTypes}
                  >
                    Remove
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                    <input
                      type="text"
                      value={t.key}
                      onChange={(e) => updateRideType(idx, { key: e.target.value })}
                      className="input"
                      placeholder="economy"
                      disabled={!canManagePricingSettings || savingRideTypes}
                    />
                    <p className="text-xs text-gray-500 mt-1">Letters, numbers, underscore only.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                    <input
                      type="text"
                      value={t.label}
                      onChange={(e) => updateRideType(idx, { label: e.target.value })}
                      className="input"
                      placeholder="Economy"
                      disabled={!canManagePricingSettings || savingRideTypes}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Base</label>
                      <input
                        type="number"
                        step="0.01"
                        value={t.baseFare}
                        onChange={(e) => updateRideType(idx, { baseFare: e.target.value })}
                        className="input"
                        disabled={!canManagePricingSettings || savingRideTypes}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Per KM</label>
                      <input
                        type="number"
                        step="0.01"
                        value={t.perKm}
                        onChange={(e) => updateRideType(idx, { perKm: e.target.value })}
                        className="input"
                        disabled={!canManagePricingSettings || savingRideTypes}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Per Min</label>
                      <input
                        type="number"
                        step="0.01"
                        value={t.perMin}
                        onChange={(e) => updateRideType(idx, { perMin: e.target.value })}
                        className="input"
                        disabled={!canManagePricingSettings || savingRideTypes}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                      <p className="text-sm font-medium">Active</p>
                      <p className="text-xs text-gray-500">Shown in the mobile app</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={t.isActive}
                        onChange={(e) => updateRideType(idx, { isActive: e.target.checked })}
                        disabled={!canManagePricingSettings || savingRideTypes}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            ))}

            {!rideTypes.length && (
              <div className="text-sm text-gray-500">No ride types configured.</div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Cancellation Policy */}
      {activeSection === 'pricing' && canManagePricingSettings && (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Cancellation Policy</h3>
            <p className="text-sm text-gray-500 mt-0.5">Fees charged when a rider cancels late, and optional penalties for drivers who cancel.</p>
          </div>
          <button
            className="btn-primary"
            onClick={saveRideTypes}
            disabled={!canManagePricingSettings || loadingRideTypes || savingRideTypes}
          >
            {savingRideTypes ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Rider late-cancel fee */}
        <div className="border rounded-lg p-4 mb-4 space-y-4">
          <h4 className="font-medium text-sm text-gray-800">Rider Cancellation Fee</h4>

          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fee Amount ({currencyCode})
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={cancellationPolicy.riderCancellationFee}
              onChange={(e) => setCancellationPolicy((p) => ({ ...p, riderCancellationFee: e.target.value }))}
              className="input"
              disabled={!canManagePricingSettings || savingRideTypes}
            />
            <p className="text-xs text-gray-500 mt-1">Charged when the rider cancels after the driver is en route. Set to 0 to disable.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Charge the fee at these stages</label>
            <div className="flex gap-6">
              {['arriving', 'arrived'].map((stage) => (
                <label key={stage} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cancellationPolicy.feeStages.includes(stage)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...cancellationPolicy.feeStages, stage]
                        : cancellationPolicy.feeStages.filter((s) => s !== stage);
                      setCancellationPolicy((p) => ({ ...p, feeStages: next }));
                    }}
                    disabled={!canManagePricingSettings || savingRideTypes}
                    className="rounded border-gray-300 text-indigo-600"
                  />
                  <span className="text-sm text-gray-700 capitalize">{stage}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Driver penalty */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-sm text-gray-800">Driver Cancellation Penalty</h4>
              <p className="text-xs text-gray-500 mt-0.5">Deduct from driver ledger when they cancel at a late stage.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={cancellationPolicy.driverPenaltyEnabled}
                onChange={(e) => setCancellationPolicy((p) => ({ ...p, driverPenaltyEnabled: e.target.checked }))}
                disabled={!canManagePricingSettings || savingRideTypes}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>

          {cancellationPolicy.driverPenaltyEnabled && (
            <div className="flex gap-4 items-end pt-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Penalty Type</label>
                <select
                  value={cancellationPolicy.driverPenaltyType}
                  onChange={(e) => setCancellationPolicy((p) => ({ ...p, driverPenaltyType: e.target.value }))}
                  className="input"
                  disabled={!canManagePricingSettings || savingRideTypes}
                >
                  <option value="fixed">Fixed ({currencyCode})</option>
                  <option value="percentage">% of Fare</option>
                </select>
              </div>
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {cancellationPolicy.driverPenaltyType === 'percentage' ? 'Percentage (%)' : `Amount (${currencyCode})`}
                </label>
                <input
                  type="number"
                  min="0"
                  step={cancellationPolicy.driverPenaltyType === 'percentage' ? '1' : '0.5'}
                  max={cancellationPolicy.driverPenaltyType === 'percentage' ? '100' : undefined}
                  value={cancellationPolicy.driverPenaltyAmount}
                  onChange={(e) => setCancellationPolicy((p) => ({ ...p, driverPenaltyAmount: e.target.value }))}
                  className="input"
                  disabled={!canManagePricingSettings || savingRideTypes}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Fare Overage Policy */}
      {activeSection === 'pricing' && canManagePricingSettings && (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Fare Overage Policy</h3>
            <p className="text-sm text-gray-500 mt-0.5">Controls how much the metered fare can exceed the quoted price, and when outstanding balances block new bookings.</p>
          </div>
          <button
            className="btn-primary"
            onClick={saveRideTypes}
            disabled={!canManagePricingSettings || loadingRideTypes || savingRideTypes}
          >
            {savingRideTypes ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Overage Cap (%)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={fareOveragePolicy.capPercent}
              onChange={(e) => setFareOveragePolicy((p) => ({ ...p, capPercent: e.target.value }))}
              className="input"
              disabled={!canManagePricingSettings || savingRideTypes}
            />
            <p className="text-xs text-gray-500 mt-1">Maximum percentage above the quoted fare that can be billed. Default: 20%.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Overage Cap Floor ({currencyCode})</label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={fareOveragePolicy.capFixed}
              onChange={(e) => setFareOveragePolicy((p) => ({ ...p, capFixed: e.target.value }))}
              className="input"
              disabled={!canManagePricingSettings || savingRideTypes}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum fixed overage allowed when the percentage cap is smaller. Default: 5 {currencyCode}.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arrears Block Threshold ({currencyCode})</label>
            <input
              type="number"
              min="0"
              step="1"
              value={fareOveragePolicy.arrearsBlockThreshold}
              onChange={(e) => setFareOveragePolicy((p) => ({ ...p, arrearsBlockThreshold: e.target.value }))}
              className="input"
              disabled={!canManagePricingSettings || savingRideTypes}
            />
            <p className="text-xs text-gray-500 mt-1">Outstanding balance above this amount blocks new bookings until settled. Set to 0 to disable. Default: 30 {currencyCode}.</p>
          </div>
        </div>
      </div>
      )}

      {/* Car Make/Model Category Rules */}
      {activeSection === 'fleet' && canManageFleetSettings && (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Car Make/Model Category Rules</h3>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              onClick={() => loadMakeOptions({ limit: 100 })}
              disabled={!canManageFleetSettings || refreshingMakes || loadingCarRules || savingCarRules}
            >
              {refreshingMakes ? 'Refreshing…' : 'Refresh Makes'}
            </button>
            <button
              className="btn-secondary"
              onClick={startCarCatalogSync}
              disabled={!canManageFleetSettings || startingCatalogSync || carCatalogSyncStatus?.isRunning}
            >
              {carCatalogSyncStatus?.isRunning ? 'Sync Running…' : startingCatalogSync ? 'Starting…' : 'Sync All Models'}
            </button>
            <button className="btn-primary" onClick={saveCarCategoryRules} disabled={!canManageFleetSettings || loadingCarRules || savingCarRules}>
              {savingCarRules ? 'Saving…' : 'Save Rules'}
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Define which ride categories are allowed or excluded for specific car make/model combinations. Leave model empty to apply rule to all models of that make.
        </p>

        {carCatalogSyncStatus && (
          <div className="mb-4 rounded-md border p-3 text-sm text-gray-700 bg-gray-50">
            <p>
              Catalog Sync: {carCatalogSyncStatus.isRunning ? 'Running' : 'Idle'}
              {carCatalogSyncStatus.total > 0 ? ` · ${carCatalogSyncStatus.processed}/${carCatalogSyncStatus.total} (${carCatalogSyncStatus.progressPercent || 0}%)` : ''}
            </p>
            <p className="text-xs text-gray-500">
              Synced: {carCatalogSyncStatus.synced || 0} · Skipped: {carCatalogSyncStatus.skipped || 0} · Failed: {carCatalogSyncStatus.failed || 0}
              {carCatalogSyncStatus.currentMake ? ` · Current: ${carCatalogSyncStatus.currentMake}` : ''}
            </p>
          </div>
        )}

        {loadingCarRules ? (
          <div className="text-sm text-gray-500">Loading car category rules…</div>
        ) : (
          <div className="space-y-4">
            <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <p className="font-medium">Add New Rule</p>
                <button className="btn-secondary" onClick={addCarRule} disabled={!canManageFleetSettings || savingCarRules}>
                  Add Rule
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Car Make</label>
                  <Select
                    options={makeOptions}
                    isDisabled={!canManageFleetSettings || savingCarRules}
                    value={newCarRule.make ? { value: newCarRule.make, label: newCarRule.make } : null}
                    onInputChange={(inputValue, meta) => {
                      if (meta?.action === 'input-change') {
                        searchMakeOptions(inputValue);
                      }
                      return inputValue;
                    }}
                    onChange={(selected) => {
                      const nextMake = selected?.value || '';
                      setNewCarRule((prev) => ({ ...prev, make: nextMake, model: '' }));
                      if (nextMake) {
                        ensureModelOptions(nextMake);
                      }
                    }}
                    placeholder="Select make"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Car Model (optional)</label>
                  <Select
                    isClearable
                    options={getModelOptions(newCarRule.make)}
                    isDisabled={!canManageFleetSettings || savingCarRules || !String(newCarRule.make || '').trim()}
                    value={newCarRule.model ? { value: newCarRule.model, label: newCarRule.model } : null}
                    onMenuOpen={() => ensureModelOptions(newCarRule.make)}
                    onChange={(selected) => setNewCarRule((prev) => ({ ...prev, model: selected?.value || '' }))}
                    placeholder={String(newCarRule.make || '').trim() ? 'Select model' : 'Select make first'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Categories</label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-white">
                    {categoryOptions.length === 0 ? (
                      <p className="text-sm text-gray-500">No categories available.</p>
                    ) : (
                      categoryOptions.map((option) => {
                        const checked = Array.isArray(newCarRule.allowedCategories) && newCarRule.allowedCategories.includes(option.value);
                        return (
                          <label key={`new-allow-${option.value}`} className="flex items-center gap-2 text-sm py-1">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!canManageFleetSettings || savingCarRules}
                              onChange={(e) => toggleNewCarRuleCategory('allowed', option.value, e.target.checked)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Excluded Categories</label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-white">
                    {categoryOptions.length === 0 ? (
                      <p className="text-sm text-gray-500">No categories available.</p>
                    ) : (
                      categoryOptions.map((option) => {
                        const checked = Array.isArray(newCarRule.excludedCategories) && newCarRule.excludedCategories.includes(option.value);
                        return (
                          <label key={`new-exclude-${option.value}`} className="flex items-center gap-2 text-sm py-1">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!canManageFleetSettings || savingCarRules}
                              onChange={(e) => toggleNewCarRuleCategory('excluded', option.value, e.target.checked)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-700">Saved Rules ({carCategoryRules.length})</p>
              <input
                className="input max-w-sm"
                placeholder="Search make or model"
                value={carRuleSearch}
                onChange={(e) => setCarRuleSearch(e.target.value)}
              />
            </div>

            {carCategoryRules.length === 0 ? (
              <div className="text-sm text-gray-500 border rounded-lg p-4">No saved rules yet. Use the “Add New Rule” section above.</div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">Make</th>
                        <th className="text-left px-3 py-2">Model</th>
                        <th className="text-left px-3 py-2">Allowed</th>
                        <th className="text-left px-3 py-2">Excluded</th>
                        <th className="text-right px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedCarRuleIndices.map((index) => {
                        const rule = carCategoryRules[index];
                        return (
                          <tr key={`${rule.make}-${rule.model}-${index}`} className="border-t">
                            <td className="px-3 py-2 font-medium">{rule.make || '-'}</td>
                            <td className="px-3 py-2">{rule.model || 'All models'}</td>
                            <td className="px-3 py-2 text-gray-600">{(rule.allowedCategories || []).join(', ') || '-'}</td>
                            <td className="px-3 py-2 text-gray-600">{(rule.excludedCategories || []).join(', ') || '-'}</td>
                            <td className="px-3 py-2">
                              <div className="flex justify-end gap-2">
                                <button
                                  className="btn-secondary"
                                  onClick={() => {
                                    setEditingCarRuleIndex(index);
                                    ensureModelOptions(rule.make);
                                  }}
                                  disabled={!canManageFleetSettings || savingCarRules}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn-secondary"
                                  onClick={() => removeCarRule(index)}
                                  disabled={!canManageFleetSettings || savingCarRules}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {pagedCarRuleIndices.length === 0 && (
                        <tr>
                          <td className="px-3 py-4 text-gray-500" colSpan={5}>
                            No rules match your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-sm">
                  <p className="text-gray-600">
                    Page {carRulePage} of {totalCarRulePages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="btn-secondary"
                      disabled={carRulePage <= 1}
                      onClick={() => setCarRulePage((prev) => Math.max(1, prev - 1))}
                    >
                      Prev
                    </button>
                    <button
                      className="btn-secondary"
                      disabled={carRulePage >= totalCarRulePages}
                      onClick={() => setCarRulePage((prev) => Math.min(totalCarRulePages, prev + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {editingCarRuleIndex != null && carCategoryRules[editingCarRuleIndex] && (
              <div className="border rounded-lg p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Editing Rule #{editingCarRuleIndex + 1}</p>
                  <button className="btn-secondary" onClick={() => setEditingCarRuleIndex(null)}>
                    Close
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Car Make</label>
                    <Select
                      options={makeOptions}
                      isDisabled={!canManageFleetSettings || savingCarRules}
                      value={carCategoryRules[editingCarRuleIndex].make ? { value: carCategoryRules[editingCarRuleIndex].make, label: carCategoryRules[editingCarRuleIndex].make } : null}
                      onInputChange={(inputValue, meta) => {
                        if (meta?.action === 'input-change') {
                          searchMakeOptions(inputValue);
                        }
                        return inputValue;
                      }}
                      onChange={(selected) => {
                        const nextMake = selected?.value || '';
                        updateCarRule(editingCarRuleIndex, { make: nextMake, model: '' });
                        if (nextMake) {
                          ensureModelOptions(nextMake);
                        }
                      }}
                      onMenuOpen={() => ensureModelOptions(carCategoryRules[editingCarRuleIndex].make)}
                      placeholder="Select make"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Car Model (optional)</label>
                    <Select
                      isClearable
                      options={getModelOptions(carCategoryRules[editingCarRuleIndex].make)}
                      isDisabled={!canManageFleetSettings || savingCarRules || !String(carCategoryRules[editingCarRuleIndex].make || '').trim()}
                      value={carCategoryRules[editingCarRuleIndex].model ? { value: carCategoryRules[editingCarRuleIndex].model, label: carCategoryRules[editingCarRuleIndex].model } : null}
                      onMenuOpen={() => ensureModelOptions(carCategoryRules[editingCarRuleIndex].make)}
                      onChange={(selected) => updateCarRule(editingCarRuleIndex, { model: selected?.value || '' })}
                      placeholder={String(carCategoryRules[editingCarRuleIndex].make || '').trim() ? 'Select model' : 'Select make first'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Categories</label>
                    <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                      {categoryOptions.length === 0 ? (
                        <p className="text-sm text-gray-500">No categories available.</p>
                      ) : (
                        categoryOptions.map((option) => {
                          const checked = Array.isArray(carCategoryRules[editingCarRuleIndex].allowedCategories) && carCategoryRules[editingCarRuleIndex].allowedCategories.includes(option.value);
                          return (
                            <label key={`edit-allow-${editingCarRuleIndex}-${option.value}`} className="flex items-center gap-2 text-sm py-1">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!canManageFleetSettings || savingCarRules}
                                onChange={(e) => toggleCarRuleCategory(editingCarRuleIndex, 'allowed', option.value, e.target.checked)}
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Excluded Categories</label>
                    <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                      {categoryOptions.length === 0 ? (
                        <p className="text-sm text-gray-500">No categories available.</p>
                      ) : (
                        categoryOptions.map((option) => {
                          const checked = Array.isArray(carCategoryRules[editingCarRuleIndex].excludedCategories) && carCategoryRules[editingCarRuleIndex].excludedCategories.includes(option.value);
                          return (
                            <label key={`edit-exclude-${editingCarRuleIndex}-${option.value}`} className="flex items-center gap-2 text-sm py-1">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!canManageFleetSettings || savingCarRules}
                                onChange={(e) => toggleCarRuleCategory(editingCarRuleIndex, 'excluded', option.value, e.target.checked)}
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Safety Settings */}
      {activeSection === 'safety' && canManageSafetySettings && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Safety Settings</h3>
            <button
              className="btn-primary"
              onClick={saveSafetySettings}
              disabled={!canManageSafetySettings || loadingSafetySettings || savingSafetySettings}
            >
              {savingSafetySettings ? 'Saving…' : 'Save Safety Settings'}
            </button>
          </div>

          {loadingSafetySettings ? (
            <div className="text-sm text-gray-500">Loading safety settings…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[
                {
                  key: 'safetyCenterEnabled',
                  label: 'Safety Center Enabled',
                  description: 'Show/hide safety center features in rider app.',
                },
                {
                  key: 'sosEnabled',
                  label: 'SOS Enabled',
                  description: 'Allow emergency SOS button usage.',
                },
                {
                  key: 'rideSharingEnabled',
                  label: 'Ride Sharing Enabled',
                  description: 'Allow users to share live ride status.',
                },
                {
                  key: 'trustedContactsEnabled',
                  label: 'Trusted Contacts Enabled',
                  description: 'Allow users to manage trusted contacts.',
                },
                {
                  key: 'safetyTipsEnabled',
                  label: 'Safety Tips Enabled',
                  description: 'Display safety tips in app.',
                },
                {
                  key: 'incidentReportEnabled',
                  label: 'Incident Report Enabled',
                  description: 'Allow incident reporting after rides.',
                },
                {
                  key: 'liveTrackingEnabled',
                  label: 'Live Tracking Enabled',
                  description: 'Allow background ride location sharing.',
                },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3 border-b">
                  <div className="pr-4">
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!safetySettings[item.key]}
                      onChange={(e) => updateSafetySetting({ [item.key]: e.target.checked })}
                      disabled={!canManageSafetySettings || savingSafetySettings}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Number</label>
                <input
                  type="text"
                  value={safetySettings.emergencyNumber}
                  onChange={(e) => updateSafetySetting({ emergencyNumber: e.target.value })}
                  className="input"
                  disabled={!canManageSafetySettings || savingSafetySettings}
                  placeholder="999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trusted Contacts Max</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={safetySettings.trustedContactsMax}
                  onChange={(e) => updateSafetySetting({ trustedContactsMax: e.target.value })}
                  className="input"
                  disabled={!canManageSafetySettings || savingSafetySettings}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Live Tracking Interval (seconds)</label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={safetySettings.locationShareIntervalSec}
                  onChange={(e) => updateSafetySetting({ locationShareIntervalSec: e.target.value })}
                  className="input"
                  disabled={!canManageSafetySettings || savingSafetySettings}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Driver Support Incident Settings */}
      {activeSection === 'support' && canManageSupportPolicySettings && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Driver Support Settings</h3>
            <div className="flex gap-2">
              <button
                className="btn-secondary"
                onClick={addIncidentType}
                disabled={!canManageSupportPolicySettings || loadingSupportSettings || savingSupportSettings}
              >
                Add Incident Type
              </button>
              <button
                className="btn-primary"
                onClick={saveSupportSettings}
                disabled={!canManageSupportPolicySettings || loadingSupportSettings || savingSupportSettings}
              >
                {savingSupportSettings ? 'Saving…' : 'Save Support Settings'}
              </button>
            </div>
          </div>

          {loadingSupportSettings ? (
            <div className="text-sm text-gray-500">Loading support settings…</div>
          ) : (
            <div className="space-y-4">
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Attachments Per Message</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  className="input"
                  value={supportSettings.maxAttachmentsPerMessage}
                  onChange={(e) => setSupportSettings((prev) => ({ ...prev, maxAttachmentsPerMessage: e.target.value }))}
                  disabled={!canManageSupportPolicySettings || savingSupportSettings}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {supportSettings.driverIncidentTypes.map((item, idx) => (
                  <div key={`${item.key}-${idx}`} className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Incident Type</div>
                      <button
                        className="btn-secondary"
                        onClick={() => removeIncidentType(idx)}
                        disabled={!canManageSupportPolicySettings || savingSupportSettings}
                      >
                        Remove
                      </button>
                    </div>

                    <input
                      className="input"
                      placeholder="key (e.g. accident)"
                      value={item.key}
                      onChange={(e) => updateIncidentType(idx, { key: e.target.value })}
                      disabled={!canManageSupportPolicySettings || savingSupportSettings}
                    />

                    <input
                      className="input"
                      placeholder="Label"
                      value={item.label}
                      onChange={(e) => updateIncidentType(idx, { label: e.target.value })}
                      disabled={!canManageSupportPolicySettings || savingSupportSettings}
                    />

                    <div className="grid grid-cols-2 gap-2 items-center">
                      <input
                        type="number"
                        className="input"
                        value={item.sortOrder}
                        onChange={(e) => updateIncidentType(idx, { sortOrder: e.target.value })}
                        disabled={!canManageSupportPolicySettings || savingSupportSettings}
                      />
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={item.isActive}
                          onChange={(e) => updateIncidentType(idx, { isActive: e.target.checked })}
                          disabled={!canManageSupportPolicySettings || savingSupportSettings}
                        />
                        Active
                      </label>
                    </div>
                  </div>
                ))}

                {!supportSettings.driverIncidentTypes.length && (
                  <div className="text-sm text-gray-500">No incident types configured.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Staff management has moved to /admins */}
      {hasAdminPermission('manage_admins') && (
        <div className="card">
          <p className="text-gray-600">
            Staff management has moved to the{' '}
            <a href="/admins" className="text-indigo-600 hover:underline">
              Admin Accounts
            </a>{' '}
            page.
          </p>
        </div>
      )}
    </div>
  );
};

export default Settings;
